/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {
  createServiceItem,
  deleteServiceItem as destroyService,
  loadServiceItems,
  updateServiceItem
} from '@ion/core/src/services/Maintenance';
import {
  MaintenanceServiceItem
} from '@ion/core/src/types/entities';
import {LoadMaintenanceServiceItemParams} from '@ion/core/src/types/services';
import {FilterModel} from '@ion/dls/src/grid/IonGridUtils';
import {getErrorMessage, translatePromptAction} from '@ion/dls/src/utils/ComponentUtils';
import {
  AsyncActionCreator,
  PageInitializeAction
} from '@/types/redux';
import {
  errorAddOptions,
  errorEditOptions,
  successAddOptions,
  successEditOptions
} from '../../routes/admin/maintenance/services/AdminMaintenanceServicesUtils';
import {getAccountId} from '../helpers';
import {toggleUserPrompt} from './user-prompt';

// name of the reducer
export const NAME                                           = 'adminMaintenanceServices';

export const INITIALIZE_ADMIN_MAINTENANCE_SERVICES          = 'INITIALIZE_ADMIN_MAINTENANCE_SERVICES';
export const RE_INITIALIZE_ADMIN_MAINTENANCE_SERVICES       = 'RE_INITIALIZE_ADMIN_MAINTENANCE_SERVICES';

// grid column state
export const SET_GRID_COLUMNS                               = 'SET_ADMIN_MAINTENANCE_SERVICES_GRID_COLUMNS';
export const UPDATE_GRID_COLUMNS                            = 'UPDATE_ADMIN_MAINTENANCE_SERVICES_GRID_COLUMNS';

// services retrieval ...
export const GET_ADMIN_MAINTENANCE_SERVICES_REQUEST         = 'GET_ADMIN_MAINTENANCE_SERVICES_REQUEST';
export const GET_ADMIN_MAINTENANCE_SERVICES_RESPONSE        = 'GET_ADMIN_MAINTENANCE_SERVICES_RESPONSE';
export const GET_ADMIN_MAINTENANCE_SERVICES_ERROR           = 'GET_ADMIN_MAINTENANCE_SERVICES_ERROR';

// service add ...
export const ADD_ADMIN_MAINTENANCE_SERVICE                  = 'ADD_ADMIN_MAINTENANCE_SERVICE';
export const ADD_ADMIN_MAINTENANCE_SERVICE_REQUEST          = 'ADD_ADMIN_MAINTENANCE_SERVICE_REQUEST';
export const ADD_ADMIN_MAINTENANCE_SERVICE_RESPONSE         = 'ADD_ADMIN_MAINTENANCE_SERVICE_RESPONSE';
export const ADD_ADMIN_MAINTENANCE_SERVICE_ERROR            = 'ADD_ADMIN_MAINTENANCE_SERVICE_ERROR';

// service edit ...
export const EDIT_ADMIN_MAINTENANCE_SERVICE                 = 'EDIT_ADMIN_MAINTENANCE_SERVICE';
export const EDIT_ADMIN_MAINTENANCE_SERVICE_REQUEST         = 'EDIT_ADMIN_MAINTENANCE_SERVICE_REQUEST';
export const EDIT_ADMIN_MAINTENANCE_SERVICE_RESPONSE        = 'EDIT_ADMIN_MAINTENANCE_SERVICE_RESPONSE';
export const EDIT_ADMIN_MAINTENANCE_SERVICE_ERROR           = 'EDIT_ADMIN_MAINTENANCE_SERVICE_ERROR';

// service delete ...
export const DELETE_ADMIN_MAINTENANCE_SERVICE                 = 'DELETE_ADMIN_MAINTENANCE_SERVICE';
export const DELETE_ADMIN_MAINTENANCE_SERVICE_REQUEST         = 'DELETE_ADMIN_MAINTENANCE_SERVICE_REQUEST';
export const DELETE_ADMIN_MAINTENANCE_SERVICE_RESPONSE        = 'DELETE_ADMIN_MAINTENANCE_SERVICE_RESPONSE';
export const DELETE_ADMIN_MAINTENANCE_SERVICE_ERROR           = 'DELETE_ADMIN_MAINTENANCE_SERVICE_ERROR';

// Grid refresh
export const ADMIN_MAINTENANCE_SCHEDULES_GRID_REFRESHED       = 'ADMIN_MAINTENANCE_SCHEDULES_GRID_REFRESHED';

// admin maintenance services filter model ...
export const SET_ADMIN_MAINTENANCE_SERVICES_GRID_FILTER_MODEL = 'SET_ADMIN_MAINTENANCE_SERVICES_GRID_FILTER_MODEL';

/**
 * @initialize
 */
export const initializeAdminMaintenanceServices = (): PageInitializeAction => {
  return {type: INITIALIZE_ADMIN_MAINTENANCE_SERVICES};
};

export const reInitializeAdminMaintenanceServices = (): PageInitializeAction => {
  return {type: RE_INITIALIZE_ADMIN_MAINTENANCE_SERVICES};
};

/**
 * @getAdminMaintenanceServices
 *
 * Invoked when the paginated list of Maintenance Services is to be retrieved.
 */
export const getAdminMaintenanceServices = ({
  pageId = 1,
  pageSize = 20,
  sort,
  filter,
  includeSubGroups = false,
  callback: callbackFn
}) => {
  return async(dispatch: Function, getState: any): Promise<any> => {
    dispatch({type: GET_ADMIN_MAINTENANCE_SERVICES_REQUEST});
    const params: LoadMaintenanceServiceItemParams = {
      accountId    : getAccountId(getState),
      hierarchy    : includeSubGroups,
      filters      : filter,
      pageId,
      pageSize,
      sort,
      convertUnits : true
    };
    try {
      const {serviceItems: items, totalCount} = await loadServiceItems(params);
      dispatch({type: GET_ADMIN_MAINTENANCE_SERVICES_RESPONSE});
      callbackFn?.({items, totalCount});
      return {items, totalCount};
    } catch (err) {
      error(err);
      dispatch({type: GET_ADMIN_MAINTENANCE_SERVICES_ERROR, error: err});
      return err;
    }
  };
};

/**
 * @addAdminMaintenanceService
 *
 * Invoked when the Add Admin Maintenance Service editor is to be displayed or removed.
 */
export const addAdminMaintenanceService = (show = true) => ({
  type : ADD_ADMIN_MAINTENANCE_SERVICE, show
});

/**
 * @addMaintenanceService
 *
 * Invoked when a new Maintenance Service is to be created; consolidating the process of
 * creation into a single transaction.
 */
export const addMaintenanceService = (
  service: MaintenanceServiceItem,
  isReadOnlyUser: boolean = false
): AsyncActionCreator => {
  return async(dispatch: Function): Promise<void> => {
    dispatch({type: ADD_ADMIN_MAINTENANCE_SERVICE_REQUEST, service});
    try {
      const created = await createServiceItem(service, true);
      dispatch({type: ADD_ADMIN_MAINTENANCE_SERVICE_RESPONSE, service: created});
      dispatch(toggleUserPrompt(successAddOptions));
    } catch (err) {
      dispatch({type: ADD_ADMIN_MAINTENANCE_SERVICE_ERROR, error: err});
      dispatch(toggleUserPrompt({
        ...errorAddOptions,
        ...({actionLabel: translatePromptAction('common:retry', isReadOnlyUser)}),
        message : getErrorMessage(err)
      }));
      error(err);
    }
  };
};

/**
 * @editAdminMaintenanceService
 *
 * Invoked when the Edit Admin Maintenance Service editor is to be displayed or removed.
 */
export const editAdminMaintenanceService = (service: MaintenanceServiceItem, show = true) => ({
  type : EDIT_ADMIN_MAINTENANCE_SERVICE, service, show
});

/**
 * @editMaintenanceService
 *
 * Invoked when an existing Maintenance Service is to be updated; consolidating the process
 * of updating into a single transaction.
 */
export const editMaintenanceService = (service: MaintenanceServiceItem, isReadOnlyUser: boolean = false): Function => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: EDIT_ADMIN_MAINTENANCE_SERVICE_REQUEST});
    try {
      const updated = await updateServiceItem(service, true);
      dispatch({type: EDIT_ADMIN_MAINTENANCE_SERVICE_RESPONSE, service: updated});
      dispatch(toggleUserPrompt(successEditOptions));
    } catch (err) {
      error(err);
      dispatch({type: EDIT_ADMIN_MAINTENANCE_SERVICE_ERROR, error: err});
      const errorOptions = {
        ...errorEditOptions,
        ...({actionLabel: translatePromptAction('common:retry', isReadOnlyUser)}),
        message : (err.status === 0 || err.status === 400)
          ? getErrorMessage(err) || translate('common:editors.serviceItemName.updateAssociationError')
          : errorEditOptions.message
      };
      dispatch(toggleUserPrompt(errorOptions));
    }
  };
};

/**
 * @deleteAdminMaintenanceService
 *
 * Invoked when the Delete Admin Maintenance Service Dialog is to be displayed or removed.
 */
export const deleteAdminMaintenanceService = (service: MaintenanceServiceItem, show = true) => ({
  type : DELETE_ADMIN_MAINTENANCE_SERVICE, service, show
});

// Invoked to reset/remove admin maintainance service error.
export const removeAdminMaintenanceServiceError = (error) => ({
  type : DELETE_ADMIN_MAINTENANCE_SERVICE_ERROR, error : error
});

/**
 * @deleteMaintenanceService
 *
 * Invoked when an existing Maintenance Service is to be deleted.
 */
export const deleteMaintenanceService = (service: MaintenanceServiceItem) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: DELETE_ADMIN_MAINTENANCE_SERVICE_REQUEST});
    try {
      const deleted = await destroyService(service);
      dispatch({type: DELETE_ADMIN_MAINTENANCE_SERVICE_RESPONSE, service: deleted});
    } catch (err) {
      dispatch({type: DELETE_ADMIN_MAINTENANCE_SERVICE_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * Invoked whenever any update such as adding, updating, removing of the service item in
 * Maintenance-Service item page.
 */
export const adminMaintenanceServiceGridRefreshed = () => {
  return {type: ADMIN_MAINTENANCE_SCHEDULES_GRID_REFRESHED};
};

// Invoked by IonGrid when the grid filter model changes.
export const setFilterModel = (model: FilterModel) => ({
  type : SET_ADMIN_MAINTENANCE_SERVICES_GRID_FILTER_MODEL, model
});
